import { Dialog } from "@headlessui/react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { getRoleLabel } from "../../constants/roles";
import {
    getTeamRoles,
    updateTeamRoleColors,
} from "../../services/teams/getTeamRoles";
import {
    TEAM_ROLE_COLOR_VALIDATION_MESSAGE,
    TEAM_ROLE_COLOR_VALIDATION_TOAST_ID,
    hasValidTeamRoleColorConfiguration,
    isValidHexColor,
} from "./teamRoleColorValidation";
import { normalizeTeamRoles } from "./teamMembersUtils";

const INVALID_HEX_TOAST_ID = "team-role-color-invalid-hex";
const SAVE_COLORS_ERROR_TOAST_ID = "team-role-colors-save-error";

const normalizeColorForInput = (colorHex) =>
    typeof colorHex === "string" ? colorHex.trim() : "";

const wasSuccessful = (response) =>
    response?.success === true && response?.data?.success !== false;

const getResponseMessage = (response, fallback) =>
    response?.status === 403
        ? "You don't have permission to do this."
        : response?.data?.message ?? response?.message ?? fallback;

export default function TeamRoleColorsModal({
    open,
    setOpen,
    teamRoles,
    onSaved,
    canWrite = true,
}) {
    const cancelButtonRef = useRef(null);
    const [draftTeamRoles, setDraftTeamRoles] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!open) return;

        setDraftTeamRoles(
            teamRoles.map((role) => ({
                ...role,
                colorHex: normalizeColorForInput(role.colorHex),
            })),
        );
    }, [open, teamRoles]);

    const closeModal = () => {
        if (isSaving) return;
        setOpen(false);
    };

    const updateRoleColor = (teamRoleId, colorHex) => {
        setDraftTeamRoles((currentRoles) =>
            currentRoles.map((role) =>
                role.id === teamRoleId ? { ...role, colorHex } : role,
            ),
        );
    };

    const clearAllColors = () => {
        setDraftTeamRoles((currentRoles) =>
            currentRoles.map((role) => ({ ...role, colorHex: "" })),
        );
    };

    const saveColors = async () => {
        if (!canWrite || isSaving) return;

        const hasInvalidColor = draftTeamRoles.some((role) => {
            const colorHex = normalizeColorForInput(role.colorHex);
            return colorHex !== "" && !isValidHexColor(colorHex);
        });

        if (hasInvalidColor) {
            toast.error("Invalid HEX. Use #RRGGBB or #RRGGBBAA.", {
                id: INVALID_HEX_TOAST_ID,
            });
            return;
        }

        if (!hasValidTeamRoleColorConfiguration(draftTeamRoles)) {
            toast.error(TEAM_ROLE_COLOR_VALIDATION_MESSAGE, {
                id: TEAM_ROLE_COLOR_VALIDATION_TOAST_ID,
            });
            return;
        }

        setIsSaving(true);

        try {
            const response = await updateTeamRoleColors(
                draftTeamRoles.map((role) => {
                    const colorHex = normalizeColorForInput(role.colorHex);
                    return {
                        teamRoleId: role.id,
                        colorHex: colorHex || null,
                    };
                }),
            );

            if (!wasSuccessful(response)) {
                const message = getResponseMessage(
                    response,
                    "Failed to save team role colors.",
                );
                toast.error(message, {
                    id:
                        message === TEAM_ROLE_COLOR_VALIDATION_MESSAGE
                            ? TEAM_ROLE_COLOR_VALIDATION_TOAST_ID
                            : SAVE_COLORS_ERROR_TOAST_ID,
                });
                return;
            }

            toast.success("Team role colors saved successfully.");
            setOpen(false);

            const refreshedResponse = await getTeamRoles();
            if (!wasSuccessful(refreshedResponse)) {
                toast.error(
                    getResponseMessage(
                        refreshedResponse,
                        "Colors were saved, but team roles could not be refreshed.",
                    ),
                    { id: SAVE_COLORS_ERROR_TOAST_ID },
                );
                return;
            }

            const responseRoles = refreshedResponse?.data?.teamRoles;
            const refreshedRoles =
                Array.isArray(responseRoles) && responseRoles.length === 0
                    ? []
                    : normalizeTeamRoles(responseRoles);
            await onSaved?.(refreshedRoles);
        } catch (error) {
            toast.error(error.message || "Failed to save team role colors.", {
                id: SAVE_COLORS_ERROR_TOAST_ID,
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog
            initialFocus={cancelButtonRef}
            open={open}
            onClose={closeModal}
            className="relative z-[60]"
        >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
            <div className="fixed inset-0 w-screen overflow-y-auto">
                <div className="flex items-center justify-center min-h-full p-4">
                    <Dialog.Panel className="relative w-full max-w-3xl px-4 pt-5 pb-4 overflow-hidden text-left bg-white rounded-lg shadow-xl sm:my-8 sm:p-6">
                        <div className="flex justify-between w-full">
                            <Dialog.Title className="w-full text-lg font-medium leading-6 text-gray-900">
                                Manage Team Role Colors
                            </Dialog.Title>
                            <button
                                type="button"
                                onClick={closeModal}
                                disabled={isSaving}
                                className="text-xl text-slate-500 hover:text-slate-700 disabled:opacity-50"
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>

                        <div className="mt-5 border rounded-sm border-slate-200">
                            <div className="hidden md:grid md:grid-cols-[1fr_140px_230px] gap-3 px-4 py-2 text-xs font-semibold uppercase bg-slate-50 text-slate-500">
                                <span>Role</span>
                                <span>Current color</span>
                                <span>Color</span>
                            </div>
                            <div className="overflow-y-auto divide-y max-h-[55vh] divide-slate-200">
                                {draftTeamRoles.map((role) => {
                                    const colorHex = normalizeColorForInput(
                                        role.colorHex,
                                    );
                                    const hasValidColor =
                                        colorHex !== "" &&
                                        isValidHexColor(colorHex);
                                    const pickerColor = hasValidColor
                                        ? colorHex.slice(0, 7)
                                        : "#6B7280";

                                    return (
                                        <div
                                            key={role.id}
                                            className="grid gap-3 px-4 py-3 md:grid-cols-[1fr_140px_230px] md:items-center"
                                        >
                                            <div className="text-sm font-medium text-slate-800">
                                                {getRoleLabel(role.name)}
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-slate-600">
                                                <span
                                                    className="block w-6 h-6 border rounded-sm border-slate-200"
                                                    style={{
                                                        backgroundColor:
                                                            hasValidColor
                                                                ? colorHex
                                                                : "#ffffff",
                                                    }}
                                                />
                                                <span className="break-all">
                                                    {colorHex || "No color"}
                                                </span>
                                            </div>
                                            <div className="flex items-center min-w-0 gap-2">
                                                <input
                                                    type="color"
                                                    value={pickerColor}
                                                    onChange={(event) =>
                                                        updateRoleColor(
                                                            role.id,
                                                            event.target.value,
                                                        )
                                                    }
                                                    className="h-10 p-1 bg-white border rounded-sm w-14 border-slate-200"
                                                    title="Pick a color"
                                                    disabled={isSaving || !canWrite}
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="#AABBCC"
                                                    value={role.colorHex}
                                                    onChange={(event) =>
                                                        updateRoleColor(
                                                            role.id,
                                                            event.target.value,
                                                        )
                                                    }
                                                    className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    disabled={isSaving || !canWrite}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="flex flex-col-reverse gap-3 mt-6 sm:flex-row sm:justify-end">
                            <button
                                type="button"
                                onClick={closeModal}
                                ref={cancelButtonRef}
                                disabled={isSaving}
                                className="inline-flex justify-center px-4 py-2 text-sm font-medium bg-white border rounded-sm shadow-sm border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={clearAllColors}
                                disabled={isSaving || !canWrite}
                                className="inline-flex justify-center px-4 py-2 text-sm font-medium bg-white border rounded-sm shadow-sm border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                                Clear all colors
                            </button>
                            <button
                                type="button"
                                onClick={saveColors}
                                disabled={isSaving || !canWrite}
                                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-teal-500 border border-transparent rounded-sm shadow-sm hover:bg-teal-600 disabled:opacity-50"
                            >
                                {isSaving ? "Saving..." : "Save colors"}
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </div>
        </Dialog>
    );
}
